/**
 * generatedGestureClip — turn a kimodo tracks payload into an ADDITIVE, in-place
 * gesture clip that can layer over whatever the state machine is driving, exactly
 * like a hand-authored `gesture_*` GLB.
 *
 * This is the additive-safety piece of the "generation-into-selection" design.
 * A generated SOMA/MetaHuman clip is a FULL-POSE body animation — its frame 0 is
 * not the rig's neutral pose — so a bare `makeClipAdditive(clip)` (which subtracts
 * the clip's OWN frame 0) leaves a wrong baseline and the scale tracks can sum to
 * 2.0 and inflate the character (the same class of bug the baked `gesture_*` GLBs
 * avoid). Instead we subtract the RIG REST POSE via the repo's existing
 * `bakeAdditive(..., { basePoseType:'ref_pose' })` path — deterministic, no
 * reliance on a "a person stands, then waves" prompt convention.
 *
 * The clip is built in 'lock' mode (no pelvis position track) so the gesture
 * plays in place; the additive delta then rides on top of the SM base pose.
 */
import { kimodoTracksToClip } from './bodyMotionClip.js'
import { bakeAdditive, buildRestPose } from './additiveBake.js'
import { playBodyGesture } from './GestureChannel.js'
import { getLiveBodyRig } from './liveBodyRig.js'

// Built-clip cache: a generated gesture that repeats (pool hit re-serving the
// same tracks) skips the rebuild + additive re-bake. Keyed by name + a cheap
// content signature — the pool serves RANDOM variants under one gesture key,
// so same-name different-tracks must miss. Insertion-ordered Map, oldest
// evicted, so a long session can't grow this unbounded.
const _clipCache = new Map()
const CLIP_CACHE_MAX = 24

function _tracksSignature(tracks) {
  const frames = tracks.frames ?? 0
  const fps = tracks.fps ?? 30
  const bones = tracks.bones ?? {}
  const names = Object.keys(bones)
  // A pelvis-only probe collides across pool variants: two arm gestures with a
  // near-static pelvis (verified against the kimodo_nod fixture, where frame0/
  // mid/last pelvis quats are near-identical) hash the same and the cache serves
  // the WRONG variant's bake. Fold a cheap FNV-style rolling float checksum over
  // EVERY bone stream (name-ordered) plus tracks.root so the signature reflects
  // the whole payload. This runs only at gesture-BUILD time (never per-frame),
  // so a full pass over ~30k floats is well within budget.
  //
  // FNV-1a on a 32-bit int accumulator: each float is quantized (fixed-point,
  // 1e-4 tolerance) to a stable integer, then its bytes are mixed in.
  let h = 0x811c9dc5 // FNV offset basis
  const foldFloat = (v) => {
    // Quantize to 1e-4 so floating jitter below display precision doesn't
    // create spurious cache misses; then mix the 32-bit quantum byte-by-byte.
    let q = Math.round((v ?? 0) * 10000) | 0
    for (let b = 0; b < 4; b++) {
      h ^= q & 0xff
      h = Math.imul(h, 0x01000193) // FNV prime, imul keeps it 32-bit
      q >>>= 8
    }
  }
  const foldStream = (s) => {
    if (!s || s.length === undefined) return
    for (let i = 0; i < s.length; i++) foldFloat(s[i])
    h ^= s.length // length-sensitivity so [a] and [a,0] differ
    h = Math.imul(h, 0x01000193)
  }
  for (let n = 0; n < names.length; n++) foldStream(bones[names[n]])
  if (tracks.root) foldStream(tracks.root)
  const hex = (h >>> 0).toString(16)
  return `${frames}:${fps}:${names.length}:${hex}`
}

/**
 * Build an additive, in-place gesture clip from kimodo tracks.
 * @param {object} tracks { fps, frames, bones, root? } — kimodo payload
 * @param {object} [opts] { scene, name, restPose }
 *   scene: the rig scene the clip binds to (defaults to the live body rig, else the
 *     GLB avatar's); required to derive the rest pose if `restPose` omitted.
 *   restPose: a prebuilt rest-pose Map (buildRestPose output) — pass it to avoid
 *     re-traversing the scene when baking a batch of clips.
 * @returns {THREE.AnimationClip} an additive clip (deltas-from-rest), in place
 */
export function buildGeneratedGestureClip(tracks, opts = {}) {
  const scene = opts.scene || resolveBodyRigScene()
  if (!scene) throw new Error('buildGeneratedGestureClip: avatar scene not ready')

  const name = opts.name || 'kimodo_gesture'
  // scene.uuid scopes the cache to the rig the additive delta was baked
  // against — a character switch/reload must not serve a stale-rest-pose bake.
  const cacheKey = `${scene.uuid}|${name}|${_tracksSignature(tracks)}`
  const cached = _clipCache.get(cacheKey)
  if (cached) return cached

  const clip = kimodoTracksToClip(tracks, {
    scene,
    name,
    rootMode: 'lock',
  })

  // The part rig's own rest, captured before its mixers posed anything — buildRestPose
  // reads the CURRENT pose, which on a driven rig is not the rest one.
  const restPose = opts.restPose || getLiveBodyRig()?.restPose || buildRestPose(scene)
  // Local-space additive, subtracting the rig rest pose (not the clip's frame 0).
  bakeAdditive(clip, { additiveType: 'local', basePoseType: 'ref_pose' }, {
    restPose,
    fps: tracks.fps || 30,
  })

  _clipCache.set(cacheKey, clip)
  if (_clipCache.size > CLIP_CACHE_MAX) {
    _clipCache.delete(_clipCache.keys().next().value)
  }
  return clip
}

/**
 * Play a kimodo-generated motion as a body gesture through the SAME selection
 * channel a hand-authored `gesture_*` clip uses: build an additive in-place clip,
 * register it on the live mixer, then hand it to GestureChannel's envelope.
 *
 * This is the generation-INTO-selection seam — a generated clip becomes
 * indistinguishable from a stock gesture at the play layer. GestureChannel forces
 * the additive blend mode and owns the fade-in/hold/fade-out weight; applyWeights
 * protects the gesture action by name while the channel is active.
 *
 * @param {object} tracks kimodo tracks payload { fps, frames, bones, root? }
 * @param {object} [opts] { name, durationMs, peakWeight, scene }
 * @returns {string} the registered clip name
 */
export function playGeneratedGesture(tracks, opts = {}) {
  const name = opts.name || 'kimodo_gesture'
  const register = getLiveBodyRig()?.register
    || (typeof window !== 'undefined' && window.__REGISTER_GENERATED_BODY_CLIP__)
  if (!register) {
    // Loud, per the UX rules — no silent fallback. The avatar mixer isn't ready.
    throw new Error('playGeneratedGesture: avatar not ready (no live body rig to register on)')
  }
  const scene = opts.scene || resolveBodyRigScene()
  const clip = buildGeneratedGestureClip(tracks, { scene, name })
  register(name, clip, { additive: true })

  const durationMs = opts.durationMs ?? Math.round(clip.duration * 1000)
  const playOpts = { durationMs }
  if (opts.peakWeight != null) playOpts.peakWeight = opts.peakWeight
  playBodyGesture(name, playOpts)
  return name
}

/** The skeleton a generated clip binds to: the declared live rig, else the GLB avatar's. */
function resolveBodyRigScene() {
  return getLiveBodyRig()?.scene || (typeof window !== 'undefined' && window.__AVATAR_SCENE__) || null
}

// Dev / test hook, mirroring window.__GEN_BODY_MOTION__ (bodyMotionNav.js): fire a
// generated gesture from a raw tracks payload without the convorcher round-trip.
if (typeof window !== 'undefined') {
  window.__PLAY_GENERATED_GESTURE__ = playGeneratedGesture
}
